import React from 'react';
import { motion } from 'framer-motion';
import { BookOpen, Globe, Truck, Heart, Clock, ArrowLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const AboutUs = () => {
  return (
    <div className="min-h-screen bg-white font-kidBody">
      {/* Hero Section */}
      <div className="bg-gradient-to-b from-[#E3F2FD] to-white pt-16 pb-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-[#5E60CE] mb-6 font-display">About Kuchipuchi</h1>
            <p className="text-xl text-[#4F86F7] mb-4 font-display">Where stories become magical memories</p>
            <div className="w-20 h-1 bg-gradient-to-r from-[#FF6B6B] to-[#FF9AA2] mx-auto rounded-full"></div>
            <div className="flex items-center justify-center space-x-2 text-sm text-gray-500 font-display mt-6">
              <Link to="/" className="hover:text-[#5E60CE]">Home</Link>
              <ChevronRight className="h-4 w-4" />
              <span>About Us</span>
            </div>
          </motion.div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="max-w-4xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-[24px] shadow-lg p-8 sm:p-10 border border-[#E6EFFD] mb-12"
          >
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 mb-6 leading-relaxed">
                Step into the magical world of Kuchipuchi, where ordinary photos bloom into extraordinary personalized storybooks. We craft enchanting tales that place your child at the center of their own adventures, creating stories as unique as they are.
              </p>
              
              <p className="text-gray-700 mb-6 leading-relaxed">
                With a global presence across the US, UK, Europe, Canada, and India, we cherish our commitment to local craftsmanship. Each book is printed in your home country, ensuring not only quick delivery but also supporting local businesses and bringing a personal touch that connects deeply with families everywhere.
              </p>
            </div>
          </motion.div>
          
          {/* Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-12">
            {[
              {
                icon: BookOpen,
                title: "Personalized Stories",
                description: "Unique books where your child becomes the hero of their own adventure",
                color: "from-[#5E60CE] to-[#4F86F7]"
              },
              {
                icon: Heart,
                title: "Crafted with Love",
                description: "Every detail of our books is designed to create a magical experience",
                color: "from-[#FF6B6B] to-[#FF9AA2]"
              },
              {
                icon: Globe,
                title: "Global Reach, Local Touch",
                description: "Operating worldwide with books printed locally in each country",
                color: "from-[#81C784] to-[#4CAF50]"
              },
              {
                icon: Truck,
                title: "Swift Delivery",
                description: "Local printing ensures your personalized books arrive quickly",
                color: "from-[#FFB800] to-[#FFD54F]"
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-[20px] shadow-md p-6 border border-[#E6EFFD] hover:shadow-lg transition-shadow"
              >
                <div className={`inline-flex items-center justify-center p-3 rounded-full bg-gradient-to-r ${feature.color} text-white mb-4`}>
                  <feature.icon className="h-6 w-6" />
                </div>
                <h3 className="text-xl font-bold text-[#5E60CE] mb-2 font-display">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
          
          {/* Mission Statement */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mt-20 mb-16"
          >
            <h2 className="text-3xl font-bold text-[#5E60CE] mb-6 font-display">Our Mission</h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto font-display">
              To create personalized stories that inspire imagination, foster a love of reading, and create lasting memories for families around the world.
            </p>
          </motion.div>
        </div>
      </div>
      
      {/* Call to Action */}
      <div className="py-16 bg-gradient-to-r from-[#6366F1] to-[#8B5CF6]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-3xl font-bold text-white mb-6 font-display"
            >
              Ready to create unforgettable memories?
            </motion.h2>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-xl text-white/80 mb-8 font-display"
            >
              Start your personalized story journey today
            </motion.p>
          </div>
        </div>
      </div>
      
      {/* Decorative bottom */}
      <div className="py-10 bg-[#F8FBFF]">
        <div className="container mx-auto px-4">
          <div className="flex justify-center space-x-8">
            {[
              { icon: BookOpen, color: "text-[#5E60CE]" },
              { icon: Heart, color: "text-[#FF9AA2]" },
              { icon: Globe, color: "text-[#4F86F7]" },
              { icon: Truck, color: "text-[#FFD54F]" },
              { icon: Clock, color: "text-[#81C784]" }
            ].map((item, index) => (
              <motion.div
                key={index}
                animate={{ y: [0, -10, 0] }}
                transition={{ 
                  duration: 2,
                  delay: index * 0.5,
                  repeat: Infinity,
                  repeatDelay: 1
                }}
                className="opacity-70"
              >
                <item.icon className={`h-8 w-8 ${item.color}`} />
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutUs; 